// /apostille/$country/$doctype — programmatic combo
// 51 countries × 15 document types = 765 high-intent landing pages
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { APOSTILLE_COUNTRIES, getApostilleCountry } from "@/data/apostille-countries";
import { TRANSLATION_DOCTYPES, getTranslationDocType } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { dynamicOgImage, hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Stamp, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, ArrowRight, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/apostille/$country/$doctype")({
  loader: ({ params }) => {
    const c = getApostilleCountry(params.country);
    const d = getTranslationDocType(params.doctype);
    if (!c || !d) throw notFound();
    return { country: params.country, doctype: params.doctype };
  },
  head: ({ params }) => {
    const c = getApostilleCountry(params.country);
    const d = getTranslationDocType(params.doctype);
    if (!c || !d) return { meta: [{ title: ctaTitle("ไม่พบหน้านี้", "/apostille/$country/$doctype", "th")}] };
    const url = `${SITE}/apostille/${c.slug}/${d.slug}`;
    const label = c.isHague ? "Apostille" : "Consular Legalization";
    const price = c.priceFromTh + d.surcharge;
    const ratingKey = `apostille/${c.slug}/${d.slug}`;
    const title = `${label} ${d.nameTh} → ${c.nameTh} ${c.flag} เริ่ม ฿${price.toLocaleString()} | NYC`;
    const desc = `บริการ ${label} ${d.nameTh} (${d.nameEn}) เพื่อใช้ที่${c.nameTh} — รวมแปล + รับรองนักแปล + MFA + ${c.authority} เสร็จ ${c.turnaround} — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    const kw = [
      `${label} ${d.nameTh}`, `${d.nameTh} ${c.nameTh}`, `แปล${d.nameTh} ${c.nameTh}`,
      `${label} ${d.nameEn}`, ...d.keywordsTh.slice(0, 3),
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/apostille/$country/$doctype", "th") }, { name: "description", content: ctaDesc(desc, "/apostille/$country/$doctype", "th")},
        { name: "keywords", content: kw.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/apostille/$country/$doctype", "th")}, { property: "og:description", content: ctaDesc(desc, "/apostille/$country/$doctype", "th")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { property: "og:image", content: dynamicOgImage({ title: `${label} ${d.nameTh} → ${c.nameTh}`, subtitle: `เริ่ม ฿${price.toLocaleString()} · ${c.turnaround}`, badge: `${d.nameEn.toUpperCase()} · ${c.nameEn.toUpperCase()}` }) },
        { property: "og:image:width", content: "1200" }, { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/apostille/${c.slug}/${d.slug}`, `/en/apostille/${c.slug}/${d.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${label} ${d.nameTh} → ${c.nameEn}`,
              serviceType: `${label} for ${d.nameEn} (Thailand → ${c.nameEn})`,
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: c.nameEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(ratingKey),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `วิธี ${label} ${d.nameTh} เพื่อใช้ที่${c.nameTh}`,
              totalTime: "P7D",
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: price },
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งเอกสารต้นฉบับ", text: `ส่ง ${d.nameTh} ฉบับจริงหรือสำเนาคัดรับรอง ผ่าน LINE @nycli — ทีมประเมินภายใน 30 นาที` },
                { "@type": "HowToStep", position: 2, name: `แปล ${d.nameTh} เป็นภาษาปลายทาง`, text: "แปลโดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม + Notary Public" },
                { "@type": "HowToStep", position: 3, name: "MFA Authentication", text: c.isHague ? "ยื่นกรมการกงสุล กระทรวงต่างประเทศ — ตรา Apostille ตาม Hague Convention" : "ยื่นกรมการกงสุลรับรองคำแปล" },
                ...(c.isHague ? [] : [{ "@type": "HowToStep", position: 4, name: "Consular Legalization", text: `ยื่นสถานทูต${c.nameTh}ในไทย ประทับตรา Legalization ขั้นสุดท้าย` }]),
                { "@type": "HowToStep", position: c.isHague ? 4 : 5, name: "จัดส่งคืน", text: "EMS / Grab / รับเองที่สำนักงานสีลม" },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Apostille / Legalization", item: `${SITE}/apostille` },
                { "@type": "ListItem", position: 3, name: `${label} → ${c.nameTh}`, item: `${SITE}/apostille/${c.slug}` },
                { "@type": "ListItem", position: 4, name: d.nameTh, item: url },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: d.faqExtra.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: ApostilleComboPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
      <Link to="/apostille" className="mt-4 inline-block text-gold underline">กลับ Apostille hub</Link>
          <QuotableStatsStrip cluster="apostille" locale="th" variant="light" />
    </main>
  ),
});

function ApostilleComboPage() {
  const { country, doctype } = Route.useLoaderData() as { country: string; doctype: string };
  const c = getApostilleCountry(country)!;
  const d = getTranslationDocType(doctype)!;
  const label = c.isHague ? "Apostille" : "Consular Legalization";
  const price = c.priceFromTh + d.surcharge;

  const otherDocs = TRANSLATION_DOCTYPES.filter((x) => x.slug !== d.slug).slice(0, 8);
  const otherCountries = APOSTILLE_COUNTRIES.filter((x) => x.slug !== c.slug && x.region === c.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/85">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/40">/</span>
            <Link to="/apostille" className="hover:text-gold">Apostille</Link>
            <span className="px-1.5 text-white/40">/</span>
            <Link to="/apostille/$country" params={{ country: c.slug }} className="hover:text-gold">{c.nameTh}</Link>
            <span className="px-1.5 text-white/40">/</span>
            <span className="text-white">{d.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> {label} · {d.nameEn} · {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{c.flag}</span>
            {label} {d.nameTh} → {c.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{d.description} ใช้ยื่นที่ {c.authority} ({c.nameEn})</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "วิธีรับรอง", v: label },
              { k: "ราคาเริ่ม", v: `฿${price.toLocaleString()}` },
              { k: "เสร็จภายใน", v: c.turnaround },
              { k: "ปลายทาง", v: c.authority },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/15 bg-white/[0.06] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5">
              <MessageCircle className="h-4 w-4" /> ปรึกษาผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          NYC Legal & Notary Service (DBD 0435567000061, ตั้งแต่ปี 2010) รับ {label} {d.nameTh} ({d.nameEn}) เพื่อใช้ที่{c.nameTh} ({c.nameEn}) ครบทุกขั้นตอน: แปลโดยนักแปลขึ้นทะเบียน + รับรอง MoJ/Notary + MFA + {c.isHague ? "ตรา Apostille" : `ประทับสถานทูต${c.nameTh}`} เริ่ม ฿{price.toLocaleString()} เสร็จ {c.turnaround} บริการสำเร็จ 2,147+ เคส
        </blockquote>

        <section className="mt-12 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เกี่ยวกับ {d.nameTh} สำหรับ {c.nameTh}</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
              {d.intent} — สำหรับใช้ที่{c.nameTh} ({c.nameEn}) เอกสารฉบับนี้ต้องผ่าน {d.certifications.join(" → ")} เพื่อให้หน่วยงาน {c.authority} รับรองความถูกต้องตามกฎหมาย
            </p>
            <ul className="mt-5 grid gap-2 sm:grid-cols-2">
              {d.destinationUses.map((u) => (
                <li key={u} className="flex items-start gap-2 text-sm text-slate-700">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
                </li>
              ))}
            </ul>
          </div>
          <aside className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา</div>
            <div className="mt-1 font-display text-3xl font-extrabold text-navy-deep">฿{price.toLocaleString()}</div>
            <div className="text-[12px] text-slate-500">หน้าแรก · เพิ่มหน้าถัดไปคิดตามจริง</div>
            <ul className="mt-4 space-y-2 text-[13px] text-slate-700">
              <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมแปล + รับรองนักแปล</li>
              <li className="flex items-start gap-2"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 2 วันทำการ +฿2,500</li>
              <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ค่าธรรมเนียม MFA / สถานทูต ตามจริง</li>
            </ul>
            <div className="mt-5"><ContactCTA /></div>
          </aside>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอน {label} {d.nameTh}</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: 1, t: `ส่ง ${d.nameTh}`, d: "LINE / Email" },
              { n: 2, t: "แปล + รับรองนักแปล", d: "MoJ Sworn / Notary" },
              { n: 3, t: c.isHague ? "MFA Apostille" : "MFA + สถานทูต", d: c.isHague ? "กรมการกงสุล" : `สถานทูต${c.nameTh}` },
              { n: 4, t: "ส่งคืน", d: "EMS / Grab" },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามพบบ่อย</h2>
          <FaqAccordion items={d.faqExtra} />
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารอื่นที่นิยม {label} ไป {c.nameTh}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherDocs.map((o) => (
              <Link key={o.slug} to="/apostille/$country/$doctype" params={{ country: c.slug, doctype: o.slug }}
                className="group flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="flex-1">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{o.nameEn}</div>
                </div>
                <ArrowRight className="mt-1 h-3.5 w-3.5 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        {otherCountries.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{label} {d.nameTh} ไปประเทศใกล้เคียง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherCountries.map((o) => (
                <Link key={o.slug} to="/apostille/$country/$doctype" params={{ country: o.slug, doctype: d.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.isHague ? "Apostille" : "Legalization"} · เริ่ม ฿{(o.priceFromTh + d.surcharge).toLocaleString()}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="apostille" locale="th" variant="light" />
    </main>
  );
}
